import React, { useState, useEffect } from 'react';
import { Mail, User, Building2, Phone, AtSign } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { cn } from '../../lib/utils';
import { photoService } from '../../services/entityServiceSingleton';
import { getEntityIconAndColor } from '../../utils/navigationIconUtils';

export interface ShadcnPersonHeroCardProps {
    entity: any;
    entityType: string;
    mode: 'view' | 'edit';
    photo?: { photoData: string | null; photoLoading: boolean };
}

const SUBTYPE_BADGE_STYLES: Record<string, string> = {
    employee: 'bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
    student: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300',
    contractor: 'bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300',
    applicant: 'bg-yellow-50 text-yellow-700 dark:bg-yellow-950 dark:text-yellow-300',
    public: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
    sponsored: 'bg-violet-50 text-violet-700 dark:bg-violet-950 dark:text-violet-300',
    shared: 'bg-teal-50 text-teal-700 dark:bg-teal-950 dark:text-teal-300',
    vacancy: 'bg-rose-50 text-rose-700 dark:bg-rose-950 dark:text-rose-300',
};

const AVATAR_PALETTE = [
    { bg: 'bg-blue-100 dark:bg-blue-900', text: 'text-blue-700 dark:text-blue-200' },
    { bg: 'bg-amber-100 dark:bg-amber-900', text: 'text-amber-700 dark:text-amber-200' },
    { bg: 'bg-emerald-100 dark:bg-emerald-900', text: 'text-emerald-700 dark:text-emerald-200' },
    { bg: 'bg-violet-100 dark:bg-violet-900', text: 'text-violet-700 dark:text-violet-200' },
    { bg: 'bg-rose-100 dark:bg-rose-900', text: 'text-rose-700 dark:text-rose-200' },
];

function getInitials(name: string | undefined): string {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return parts[0][0]?.toUpperCase() || '?';
}

function hashCode(s: string): number {
    let h = 0;
    for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
    return Math.abs(h);
}

function resolveEntityType(entity: any): string {
    const et = entity?.EntityType || entity?.entityType;
    if (typeof et === 'string') return et;
    if (et && typeof et === 'object') return et.DisplayName || et.Id || '';
    return '';
}

function InfoItem({ icon: Icon, label, value }: { icon: React.FC<any>; label: string; value: string }) {
    if (!value) return null;
    return (
        <div className="flex items-center gap-2 min-w-0">
            <Icon size={14} className="shrink-0 text-muted-foreground" />
            <span className="text-xs text-muted-foreground shrink-0">{label}</span>
            <span className="text-xs text-foreground truncate" title={value}>{value}</span>
        </div>
    );
}

const ShadcnPersonHeroCard: React.FC<ShadcnPersonHeroCardProps> = ({ entity, entityType, mode, photo: photoProp }) => {
    const [localPhotoData, setLocalPhotoData] = useState<string | null>(null);
    const [localPhotoLoading, setLocalPhotoLoading] = useState(true);

    const entityId = entity?.Id || entity?.id;
    const displayName = entity?.DisplayName || entity?.displayName || '';
    const entityTypeStr = resolveEntityType(entity) || entityType || 'Person';
    const badgeStyle = SUBTYPE_BADGE_STYLES[entityTypeStr.toLowerCase()] || 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300';
    const { Icon: EntityIcon, color: iconColor } = getEntityIconAndColor(entityTypeStr);
    const palette = AVATAR_PALETTE[hashCode(displayName || 'x') % AVATAR_PALETTE.length];

    const email = entity?.Email || entity?.email || '';
    const upn = entity?.UserPrincipalName || entity?.userPrincipalName || '';
    const department = entity?.Department || entity?.department || entity?.JobTitle || entity?.jobTitle || '';
    const phone = entity?.OfficePhone || entity?.officePhone || entity?.MobilePhone || entity?.mobilePhone || '';
    const accountName = entity?.AccountName || entity?.accountName || '';

    const photoData = photoProp?.photoData ?? localPhotoData;
    const photoLoading = photoProp ? photoProp.photoLoading : localPhotoLoading;

    useEffect(() => {
        if (photoProp?.photoData && entityId) {
            photoService.cachePhoto(entityId, photoProp.photoData);
        }
    }, [photoProp?.photoData, entityId]);

    useEffect(() => {
        if (photoProp || !entityId) return;
        const cached = photoService.getCachedPhoto(entityId);
        if (cached?.Photo) {
            setLocalPhotoData(cached.Photo);
            setLocalPhotoLoading(false);
            return;
        }
        setLocalPhotoLoading(false);
    }, [entityId, photoProp]);

    if (!entity) return null;

    return (
        <div className="rounded-lg border border-border bg-card p-5 mb-4">
            <div className="flex items-start gap-5">
                {/* Avatar */}
                <div className="shrink-0">
                    {photoLoading ? (
                        <div className="h-20 w-20 rounded-full bg-muted animate-pulse" />
                    ) : (
                        <Avatar className={cn('h-20 w-20', !photoData && palette.bg)}>
                            {photoData && (
                                <AvatarImage src={`data:image/jpeg;base64,${photoData}`} alt={displayName} />
                            )}
                            <AvatarFallback className={cn('text-xl font-bold', palette.bg, palette.text)}>
                                {getInitials(displayName)}
                            </AvatarFallback>
                        </Avatar>
                    )}
                </div>

                {/* Info */}
                <div className="flex flex-col gap-1.5 flex-1 min-w-0">
                    <div className="flex items-center gap-2.5 flex-wrap">
                        <h2 className="text-lg font-bold text-foreground truncate leading-tight">
                            {displayName || 'Loading...'}
                        </h2>
                        <span className={cn(
                            'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium shrink-0',
                            badgeStyle,
                        )}>
                            {entityTypeStr}
                        </span>
                        {mode === 'edit' && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border border-yellow-400 text-yellow-600 dark:text-yellow-400 shrink-0">
                                Editing
                            </span>
                        )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1.5 mt-1">
                        <InfoItem icon={Mail} label="Email" value={email} />
                        <InfoItem icon={AtSign} label="UPN" value={upn} />
                        <InfoItem icon={Building2} label="Dept" value={department} />
                        {phone && <InfoItem icon={Phone} label="Phone" value={phone} />}
                        {accountName && <InfoItem icon={User} label="Account" value={accountName} />}
                    </div>
                </div>

                {/* Watermark icon */}
                <div className="shrink-0 self-center opacity-10">
                    <EntityIcon size={48} color={iconColor} />
                </div>
            </div>
        </div>
    );
};

export { ShadcnPersonHeroCard };
